import { Component } from '@theme/component';

/**
 * Custom element managing the 2-Column Promotional Offer Banner Popup and storewide price badges.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} dialogComponent
 * @property {HTMLElement} [initialView]
 * @property {HTMLElement} [claimedView]
 * @property {HTMLElement} [codeDisplay]
 * @property {HTMLElement} [copyBtn]
 * @property {HTMLInputElement} [emailInput]
 *
 * @extends {Component<Refs>}
 */
export class OfferBannerPopupComponent extends Component {
  requiredRefs = [];

  connectedCallback() {
    super.connectedCallback();

    const isClaimed = localStorage.getItem('reflekt_discount_claimed') === 'true';
    const isSeen = localStorage.getItem('reflekt_offer_popup_seen') === 'true';
    const discountCode = this.dataset.discountCode || 'REFLEKT5';
    const delay = parseInt(this.dataset.delay || '1500', 10);
    const designMode = this.dataset.designMode === 'true';
    const testMode = this.dataset.testMode === 'true' || designMode;

    // If already claimed, show badges and apply discount to checkout session
    if (isClaimed) {
      document.body.setAttribute('data-offer-claimed', 'true');
      this.#applyDiscountToSession(discountCode);
      this.#showClaimedUI();
    }

    // Show popup only if: testMode OR (not seen AND not claimed)
    if (this.dataset.enablePopup === 'true') {
      if (testMode || (!isSeen && !isClaimed)) {
        this._popupTimer = setTimeout(() => {
          this.openModal();
        }, delay);
      }
    }

    // Event delegation: capture ALL clicks inside this component
    // This handles the close button reliably regardless of when it's rendered
    this.addEventListener('click', this.#handleComponentClick);

    document.addEventListener('submit', this.#handleGlobalSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._popupTimer) clearTimeout(this._popupTimer);
    this.removeEventListener('click', this.#handleComponentClick);
    document.removeEventListener('submit', this.#handleGlobalSubmit);
  }

  /**
   * Apply the Shopify discount code to the current browser session.
   */
  #applyDiscountToSession(code) {
    if (!code) return;
    fetch(`/discount/${encodeURIComponent(code)}`, { method: 'GET', credentials: 'same-origin' })
      .catch(() => { /* silent */ });
  }

  /**
   * Delegated click handler for the entire component.
   * Catches close button clicks regardless of DOM timing.
   */
  #handleComponentClick = (event) => {
    const closeBtn = event.target.closest('.offer-banner__close');
    if (closeBtn) {
      event.preventDefault();
      event.stopPropagation();
      this.closePopup();
    }
  };

  #showClaimedUI() {
    if (this.refs.initialView) {
      this.refs.initialView.classList.add('hidden');
    }
    if (this.refs.claimedView) {
      this.refs.claimedView.classList.remove('hidden');
    }
  }

  claimOffer() {
    const discountCode = this.dataset.discountCode || 'REFLEKT5';
    localStorage.setItem('reflekt_discount_claimed', 'true');
    localStorage.setItem('reflekt_offer_popup_seen', 'true');
    document.body.setAttribute('data-offer-claimed', 'true');
    this.#applyDiscountToSession(discountCode);
    this.#showClaimedUI();
  }

  #handleGlobalSubmit = (event) => {
    const target = event.target;
    if (!target || !(target instanceof HTMLFormElement)) return;

    const isOfferForm = target.id === 'OfferBannerCustomerForm' || target.classList.contains('offer-banner__form');
    const isFooterForm = target.classList.contains('email-signup__form') || (target.action && target.action.includes('/contact'));

    if (isOfferForm || isFooterForm) {
      const emailInput = target.querySelector('input[type="email"]');
      if (emailInput && emailInput.value) {
        this.claimOffer();
      }
    }
  };

  openModal() {
    const dialogComponent = this.refs.dialogComponent || this.querySelector('dialog-component');
    if (dialogComponent && typeof dialogComponent.showDialog === 'function') {
      dialogComponent.showDialog();
    }
  }

  closePopup() {
    localStorage.setItem('reflekt_offer_popup_seen', 'true');

    // Try dialog-component's closeDialog first
    const dialogComponent = this.refs.dialogComponent || this.querySelector('dialog-component');
    if (dialogComponent && typeof dialogComponent.closeDialog === 'function') {
      dialogComponent.closeDialog();
      return;
    }

    // Fallback: directly close the native <dialog> element
    const dialog = this.querySelector('dialog');
    if (dialog && dialog.open) {
      dialog.close();
    }
  }

  copyCode() {
    const code = this.dataset.discountCode || 'REFLEKT5';
    if (navigator.clipboard) {
      navigator.clipboard.writeText(code).then(() => {
        if (this.refs.copyBtn) {
          const originalText = this.refs.copyBtn.textContent;
          this.refs.copyBtn.textContent = '✓ Copied!';
          setTimeout(() => {
            if (this.refs.copyBtn) this.refs.copyBtn.textContent = originalText;
          }, 2000);
        }
      });
    }
  }
}

if (!customElements.get('offer-banner-popup-component')) {
  customElements.define('offer-banner-popup-component', OfferBannerPopupComponent);
}
